import { Box, Button, Typography, useTheme } from '@mui/material';
import { Camera, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { get } from '@/services/api';
import LoadingDialog from '@/components/LoadingDialog';
import { LogoutModal } from '@/pages/v2/profile/components/logout-modal';
import { BackButton } from '@/components/BackButton';
import { getCardSx } from '@/theme/utils';
import { UserPhoto } from '@/pages/HomePage/components/UserPhoto';
import { useBreakpoint } from '@/hooks';

export const Header = ({
  fullName,
  profileImageUrl,
}: {
  fullName: string;
  profileImageUrl?: string;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const [openLogoutModal, setOpenLogoutModal] = useState(false);
  const [closeSession, setCloseSession] = useState(false);
  const { isDesktop } = useBreakpoint();

  const logout = () => {
    setOpenLogoutModal(true);
  };

  const handleLogout = async () => {
    setCloseSession(true);
    await get('users/logout');
    setCloseSession(false);
    localStorage.clear();
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    window.location.href = `${import.meta.env.VITE_APP_LOGIN_HOST}/logout/`!;
  };

  return (
    <>
      <LoadingDialog open={closeSession} />
      <LogoutModal
        openLogoutModal={openLogoutModal}
        setOpenLogoutModal={setOpenLogoutModal}
        handleLogout={handleLogout}
      />

      {/* Nav row */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: isDesktop ? 'end' : 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <BackButton fallback="/" />
        <Button
          onClick={logout}
          variant="text"
          size="small"
          startIcon={<LogOut size={14} color={theme.palette.error.main} />}
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: theme.fontSizes.caption,
            color: theme.palette.error.main,
            textTransform: 'none',
            '&:active': { transform: 'scale(0.97)' },
          }}
        >
          {t('general.logout')}
        </Button>
      </Box>

      {/* Profile card */}
      <Box
        sx={{
          ...getCardSx(theme),
          cursor: 'default',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 2,
        }}
      >
        {/* Avatar con badge de cámara */}
        <Box
          sx={{
            position: 'relative',
            flexShrink: 0,
            '&:active': { opacity: 0.85 },
          }}
        >
          <UserPhoto
            onClick={() =>
              navigate(`/profile/image?image_url=${profileImageUrl}`)
            }
          />
          <Box
            onClick={() =>
              navigate(`/profile/image?image_url=${profileImageUrl}`)
            }
            sx={{
              position: 'absolute',
              bottom: -2,
              right: -2,
              bgcolor: theme.palette.primary.main,
              borderRadius: '50%',
              width: 22,
              height: 22,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `2px solid white`,
              boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
              cursor: 'pointer',
            }}
          >
            <Camera size={11} color="white" />
          </Box>
        </Box>

        {/* Greeting + Name */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: theme.fontSizes.caption,
              color: theme.palette.text.disabled,
              lineHeight: 1.3,
            }}
          >
            {`${t('general.hello')}!`}
          </Typography>
          <Typography
            noWrap
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: theme.fontSizes.subtitle,
              fontWeight: 600,
              color: theme.palette.text.primary,
              lineHeight: 1.35,
              letterSpacing: '-0.015em',
            }}
          >
            {fullName}
          </Typography>
        </Box>
      </Box>
    </>
  );
};
